import { useEffect, useId, useState } from 'react';
import type {
  ContradictionFieldRef,
  ContradictionFinding,
} from '../../../services/api/types.gen';
import { resolveCategoryLabel } from '../OASISFormLabels';
import { ContradictionRuleTypeIcon } from './ContradictionRuleTypeIcon';
import {
  contradictionLocationIsOnCategory,
  primaryCategoryForContradictionLocation,
} from './contradictionNavigation';
import {
  formatCodeWithDescription,
  lookupIcdDescriptions,
} from './lookupIcdDescriptions';

// The chip stays visually small so the row reads tight, and `before:-inset-y-1`
// grows the touch target to 28px without taking any layout space — it is the
// primary finding→field affordance on a ~390px phone.
const locationChipClassName =
  "relative inline-flex items-center rounded-sm px-1.5 py-0.5 text-xs leading-4 text-muted-foreground/65 transition-colors before:absolute before:inset-x-0 before:-inset-y-1 before:content-[''] hover:text-muted-foreground hover:underline";

export interface ContradictionFindingCardProps {
  finding: ContradictionFinding;
  currentCategory: string | null;
  qidToCategoryMap: Record<string, string>;
  labelByCategory: Record<string, string>;
  /**
   * Section chip: highlight that location, switching section first when it
   * lives elsewhere (same scroll/highlight path as a CMS jump).
   */
  onHighlightLocation: (location: ContradictionFieldRef) => void;
  /**
   * Dismiss (true) or restore (false) this finding. Many rules warn about the
   * *appearance* of a contradiction, so a reviewed chart needs a way to stop
   * one from counting without losing it.
   */
  onSetDismissed: (ruleId: string, dismissed: boolean) => void | Promise<void>;
}

export function ContradictionFindingCard({
  finding,
  currentCategory,
  qidToCategoryMap,
  labelByCategory,
  onHighlightLocation,
  onSetDismissed,
}: ContradictionFindingCardProps) {
  const guidanceId = useId();
  const [suggestionLines, setSuggestionLines] = useState<string[]>([]);
  const [pending, setPending] = useState(false);
  const suggestionCodes = finding.suggested_diagnosis_codes || [];
  const suggestionCodesKey = suggestionCodes.join('|');

  useEffect(() => {
    let cancelled = false;
    const codes = suggestionCodesKey
      ? suggestionCodesKey.split('|')
      : ([] as string[]);
    if (codes.length === 0) {
      setSuggestionLines([]);
      return;
    }
    void lookupIcdDescriptions(codes).then(descriptions => {
      if (cancelled) return;
      setSuggestionLines(
        codes.map(code => formatCodeWithDescription(code, descriptions))
      );
    });
    return () => {
      cancelled = true;
    };
  }, [suggestionCodesKey]);

  const locations = finding.error_locations || [];
  const displayTypes = finding.types.includes('reimbursement_impact')
    ? finding.types.filter(type => type !== 'coding_oasis_error')
    : finding.types;

  // The card body is inert — no hover border, so nothing suggests the card
  // itself is clickable. `role='group'` + `aria-labelledby` keeps the chips
  // associated with their finding text without adding a tab stop back.
  return (
    <div
      role='group'
      aria-labelledby={guidanceId}
      // Dismissed findings stay on the QA tab, faded, below the active ones —
      // "not gone forever", just not counting.
      className={`group rounded-md border border-border/80 bg-background/80 text-xs${
        finding.dismissed ? ' opacity-50' : ''
      }`}
    >
      <div className='px-2.5 py-1.5 text-left'>
        <div className='flex items-start gap-2'>
          <div className='mt-0.5 flex shrink-0 items-center gap-0.5 text-muted-foreground'>
            {displayTypes.map(type => (
              <ContradictionRuleTypeIcon key={type} type={type} />
            ))}
          </div>
          <div className='min-w-0 flex-1'>
            <p
              id={guidanceId}
              className='font-medium leading-snug text-foreground/90'
            >
              {finding.guidance}
            </p>
            {/* Multi-code suggestions stay on the finding but are not rendered —
                a semicolon-joined list of codes is unreadable in the card. */}
            {suggestionCodes.length === 1 && suggestionLines.length > 0 && (
              <p className='mt-0.5 leading-snug text-muted-foreground/65'>
                Suggested diagnosis: {suggestionLines[0]}
              </p>
            )}
          </div>
          <span className='shrink-0 select-none font-mono text-[10px] text-muted-foreground/40 opacity-0 transition-opacity group-hover:opacity-100'>
            {finding.rule_id}
          </span>
        </div>
      </div>

      {/* `gap-y-1` keeps the chips' invisible touch areas from overlapping when
          the row wraps on a narrow screen. The dismiss control shares this row
          so a finding gains no extra vertical space on a phone. */}
      <div className='flex flex-wrap items-center gap-x-1.5 gap-y-1 border-t border-border/60 px-2.5 py-0.5'>
        {locations.map((location, index) => {
          const onCurrent = contradictionLocationIsOnCategory(
            location,
            currentCategory,
            qidToCategoryMap
          );
          const sectionId = primaryCategoryForContradictionLocation(
            location,
            qidToCategoryMap
          );
          const sectionLabel = sectionId
            ? resolveCategoryLabel(sectionId, labelByCategory)
            : null;
          return (
            <span
              key={`${location.source}-${location.oasis_item_id ?? location.field ?? location.label}-${index}`}
              className='inline-flex items-center gap-0.5'
            >
              {index > 0 && (
                <span
                  className='text-[10px] leading-4 text-muted-foreground/40'
                  aria-hidden
                >
                  ·
                </span>
              )}
              {sectionLabel ? (
                <button
                  type='button'
                  className={locationChipClassName}
                  onClick={() => onHighlightLocation(location)}
                  aria-label={
                    onCurrent
                      ? `Highlight ${location.label} on ${sectionLabel}`
                      : `Go to ${sectionLabel} and highlight ${location.label}`
                  }
                >
                  {/* No margin or separate color on the parenthetical: the
                        whole chip recolors and underlines as one on hover. */}
                  {sectionLabel} ({location.label})
                </button>
              ) : (
                <span className='px-1.5 py-0.5 text-xs leading-4 text-muted-foreground/65'>
                  {location.label}
                </span>
              )}
            </span>
          );
        })}
        <button
          type='button'
          className={`${locationChipClassName} ml-auto disabled:pointer-events-none disabled:opacity-50`}
          // Disabled while its own call is in flight: a double-tap would
          // otherwise fire dismiss and restore back to back, and the two can
          // land at the server out of order.
          disabled={pending}
          onClick={() => {
            setPending(true);
            void Promise.resolve(
              onSetDismissed(finding.rule_id, !finding.dismissed)
            ).finally(() => setPending(false));
          }}
        >
          {finding.dismissed ? 'Restore' : 'Dismiss'}
        </button>
      </div>
    </div>
  );
}
